import { EntityBase } from '@provision/entity-types';
import { ApolloClient, DocumentNode } from '@provision/graphql-client';
import { IGNORE_SORTING_REFETCH_ATTRIBUTES } from 'src/graphql/setup/constants';
import { RegisteredQuery } from 'src/graphql/setup/query-registry';
import { EvictRelevantQueriesParams } from 'src/graphql/setup/types';
import { Records } from 'src/graphql/types';

export function isEventRelevantToSorting(params: {
  operation: EvictRelevantQueriesParams['operation'];
  changedFields: string[];
  variables: RegisteredQuery['variables'];
  onFirstPage?: boolean;
}) {
  const { operation, changedFields, variables, onFirstPage } = params;

  if (operation === 'create') {
    // create operations must be evicted by filter triggers only
    // no need to re-sort since record isn't a part of the table yet
    return false;
  }

  const sorting = variables.params?.sorting ?? variables.sorting;

  // if there's no explicit sorting configured, we always refetch on update
  if (!sorting) {
    return true;
  }

  // gather the list of attributes we're sorting by
  const sortAttrs = (Array.isArray(sorting) ? sorting : [sorting])
    .map((s) => s.attribute)
    .filter(Boolean);

  const relevantSortAttrs = onFirstPage
    ? sortAttrs.filter((attr) => !IGNORE_SORTING_REFETCH_ATTRIBUTES.includes(attr))
    : sortAttrs;

  // if there are relevant sort attributes, and none of them changed - skip refetch
  if (
    relevantSortAttrs.length < 0 ||
    relevantSortAttrs.every((attr) => !changedFields.includes(attr))
  ) {
    return false;
  }

  // otherwise—yes, the change could affect ordering - refetch
  return true;
}

export function isEventRelevantToSearch(variables: RegisteredQuery['variables']) {
  const searchParams = variables.params?.searchParams ?? variables.searchParams;

  // search matches are computed server side against searchable documents,
  // so relevance can't be evaluated client side - while a search term is
  // active, any event may change the results
  return Boolean(searchParams?.searchTerm);
}

export function skipUnrelatedUpdateEvents(params: {
  operation: EvictRelevantQueriesParams['operation'];
  variables: RegisteredQuery['variables'];
}) {
  const { variables, operation } = params;
  if (operation !== 'update') {
    return false;
  }

  // single limit queries should not be evicted by update operations
  if (variables.params?.limit === 1 || variables.limit === 1) {
    return true;
  }

  return false;
}

export function entityMatches(rawQueryEntity: string, rawEventEntity: string) {
  return rawQueryEntity.toLowerCase() === rawEventEntity.toLowerCase();
}

export function isRecordInCacheList({
  apolloClient,
  document,
  variables,
  id,
}: {
  apolloClient: ApolloClient<unknown>;
  document: DocumentNode;
  variables: RegisteredQuery['variables'];
  id: string;
}) {
  try {
    // readQuery will only look in the cache (it won’t hit the network)
    const data = apolloClient.cache.readQuery<Record<string, Records<EntityBase>>>({
      query: document,
      variables,
    });
    const queryName = Object.keys(data ?? {})[0];

    return data?.[queryName].records?.some((item) => item.id === id);
  } catch (e) {
    console.error(e);
    // either the query isn’t in the cache yet,
    // or the shape was unexpected → treat as “not present”
    return false;
  }
}
